import React, { useEffect, useMemo, useState } from 'react';
import { Target } from 'lucide-react';
import { getPublicDonateGoal, DonateGoal } from '../../../api/payment/donate-goal.api';
import DonateGoalWidget from '../../../components/donate-goal/donate-goal-widget';
import { parseDonateGoalTheme } from '../../../components/donate-goal/donate-goal-theme';

/**
 * Mục tiêu donate hiển thị cho NGƯỜI XEM trên trang creator.
 * Trước đây mục tiêu chỉ hiện trên overlay livestream — ai vào trang cá nhân mà không
 * xem stream thì không biết creator đang có mục tiêu gì, mất một điểm đẩy donate.
 * Dùng chung giao diện creator đã chọn cho live; riêng mẫu "Trong suốt" (sinh ra để đè lên
 * video) thì đổi sang mẫu "Sáng" cho dễ đọc trên trang.
 */
const CreatorDonateGoal: React.FC<{ userId?: string }> = ({ userId }) => {
  const [goal, setGoal] = useState<DonateGoal | null>(null);

  useEffect(() => {
    if (!userId) return;
    getPublicDonateGoal(userId)
      .then(setGoal)
      .catch(() => setGoal(null));
  }, [userId]);

  const theme = useMemo(() => {
    const parsed = parseDonateGoalTheme(goal?.theme);
    if (parsed.preset !== 'clear') return parsed;
    // Màu chữ/nền chọn cho video thường là chữ trắng: đặt lên nền sáng sẽ mất chữ, nên chỉ giữ màu thanh.
    return { preset: 'light' as const, barFrom: parsed.barFrom, barTo: parsed.barTo };
  }, [goal?.theme]);

  // Chưa đặt mục tiêu / đã tắt -> BE trả exists=false, không hiện gì.
  if (!goal?.exists) return null;

  return (
    <div className="mt-8 box rounded-2xl p-4 md:p-6">
      <div className="flex items-center gap-2">
        <Target className="text-blue-600" size={20} />
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
          Mục tiêu donate
        </h3>
      </div>

      <div className="mt-3">
        <DonateGoalWidget
          size="card"
          title={goal.title}
          description={goal.description}
          currentAmount={goal.currentAmount}
          targetAmount={goal.targetAmount}
          donationCount={goal.donationCount}
          isExpired={goal.isExpired}
          isActive={goal.isActive}
          theme={theme}
          showStatus
        />
      </div>
    </div>
  );
};

export default CreatorDonateGoal;
